// Page thumbnails of a Sample_Packet (blueprint-sets Req 10.1, 12.8; design "SetProposalReview").
// The packet is loaded once per review by `PacketDocument` from the proposal's `documentUrl` (the documents bucket,
// already in the CSP, so no new origin) and shared by every `PageThumbnails`: PDF pages through `react-pdf` `Page` at
// 96 px, TIFF pages decoded with UTIF as in `DocumentViewer`, and PNG/JPEG (one page) as an image.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Page, pdfjs } from 'react-pdf';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';
import * as UTIF from 'utif';

import { pdfOptions } from '../lib/pdfjsWorker';

export const THUMBNAIL_WIDTH = 96;
/** Thumbnails shown per list; the rest are summarized as "+N more pages". */
export const MAX_THUMBNAILS = 24;

type PdfProxy = Awaited<ReturnType<typeof pdfjs.getDocument>['promise']>;
type Tiff = { buf: ArrayBuffer; ifds: UTIF.IFD[] };

type Loaded =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'pdf'; pdf: PdfProxy }
  | { kind: 'tiff'; tiff: Tiff }
  | { kind: 'image'; url: string };

interface PacketSource {
  url: string;
  contentType: string;
  name: string;
}

const PacketCtx = createContext<{ source: PacketSource; loaded: Loaded } | null>(null);

/** Loads the packet once for the thumbnails below it. Renders its children at once, whatever the load state. */
export function PacketDocument({ url, contentType, name, children }: PacketSource & { children: ReactNode }) {
  const [state, setState] = useState<{ url: string; loaded: Loaded }>({
    url,
    loaded:
      contentType === 'application/pdf' || contentType === 'image/tiff' ? { kind: 'loading' } : { kind: 'image', url },
  });
  // A new URL starts a new load (state adjusted during render, not in an effect).
  if (state.url !== url) {
    setState({
      url,
      loaded:
        contentType === 'application/pdf' || contentType === 'image/tiff'
          ? { kind: 'loading' }
          : { kind: 'image', url },
    });
  }
  useEffect(() => {
    let cancelled = false;
    const done = (loaded: Loaded) => {
      if (!cancelled) setState({ url, loaded });
    };
    if (contentType === 'application/pdf') {
      const task = pdfjs.getDocument({ url, ...pdfOptions });
      task.promise.then(
        (pdf) => done({ kind: 'pdf', pdf }),
        () => done({ kind: 'error' }),
      );
      return () => {
        cancelled = true;
        void task.destroy();
      };
    }
    if (contentType === 'image/tiff') {
      fetch(url)
        .then((r) => r.arrayBuffer())
        .then((buf) => done({ kind: 'tiff', tiff: { buf, ifds: UTIF.decode(buf) } }))
        .catch(() => done({ kind: 'error' }));
    }
    return () => {
      cancelled = true;
    };
  }, [url, contentType]);
  return (
    <PacketCtx.Provider value={{ source: { url, contentType, name }, loaded: state.loaded }}>
      {children}
    </PacketCtx.Provider>
  );
}

function TiffThumb({ tiff, page, label }: { tiff: Tiff; page: number; label: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const ifd = tiff.ifds[page - 1];
    const canvas = ref.current;
    if (!canvas) return;
    try {
      if (!ifd) throw new Error('no page');
      UTIF.decodeImage(tiff.buf, ifd);
      const rgba = UTIF.toRGBA8(ifd);
      canvas.width = ifd.width;
      canvas.height = ifd.height;
      canvas.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(rgba), ifd.width, ifd.height), 0, 0);
    } catch {
      // Shown as the text fallback below; a page beyond the TIFF's IFDs or an undecodable one.
      queueMicrotask(() => setError(true));
    }
  }, [tiff, page]);
  if (error) return <Box variant="small">No preview</Box>;
  return <canvas ref={ref} role="img" aria-label={label} style={{ width: THUMBNAIL_WIDTH, height: 'auto' }} />;
}

function Thumb({ page, loaded, name }: { page: number; loaded: Loaded; name: string }) {
  const label = `${name}, page ${page}`;
  switch (loaded.kind) {
    case 'loading':
      return <Spinner />;
    case 'error':
      return <Box variant="small">No preview</Box>;
    case 'pdf':
      return (
        <div role="img" aria-label={label}>
          <Page
            pdf={loaded.pdf}
            pageNumber={page}
            width={THUMBNAIL_WIDTH}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            loading={<Spinner />}
            error={<Box variant="small">No preview</Box>}
          />
        </div>
      );
    case 'tiff':
      return <TiffThumb tiff={loaded.tiff} page={page} label={label} />;
    case 'image':
      return page === 1 ? (
        <img src={loaded.url} alt={label} width={THUMBNAIL_WIDTH} />
      ) : (
        <Box variant="small">No preview</Box>
      );
  }
}

/** Thumbnails of the given 1-based pages, each captioned "Page n". Needs a `PacketDocument` above it; none renders nothing. */
export function PageThumbnails({ pages }: { pages: number[] }) {
  const ctx = useContext(PacketCtx);
  if (!ctx || pages.length === 0) return null;
  const shown = pages.slice(0, MAX_THUMBNAILS);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
      {shown.map((p) => (
        <figure key={p} style={{ margin: 0, width: THUMBNAIL_WIDTH }}>
          <Thumb page={p} loaded={ctx.loaded} name={ctx.source.name} />
          <figcaption>
            <Box variant="small">Page {p}</Box>
          </figcaption>
        </figure>
      ))}
      {pages.length > shown.length && <Box variant="small">+{pages.length - shown.length} more pages</Box>}
    </div>
  );
}
